import { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  LogOut,
  Scale,
  Settings,
  User,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { logout } from 'features/auth/authSlice';
import { signOut, isSupabaseConfigured } from 'features/auth/api/authService';
import { ROUTES } from 'constants/routes';
import { learnerNav } from 'constants/copy';
import { useAuth } from 'schools/parashari/contexts/AuthContext';
import { useStudentProfile } from 'schools/parashari/hooks/useStudentProfile';
import { LearnerAvatar } from 'schools/parashari/components/profile/LearnerAvatar';
import { useLearnerDisplayName } from 'schools/parashari/hooks/useLearnerDisplayName';
import { usePlatformTheme } from 'schools/parashari/hooks/usePlatformTheme';
import { useDismissOnOutside } from './useDismissOnOutside';

type LearnerNavIdentityProps = {
  profileOpen: boolean;
  onProfileOpenChange: (open: boolean) => void;
  onNavigate?: () => void;
  onOpenEthics?: () => void;
};

export function LearnerNavIdentity({
  profileOpen,
  onProfileOpenChange,
  onNavigate,
  onOpenEthics,
}: LearnerNavIdentityProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const platformUser = useAppSelector((s) => s.auth.user);
  const { user } = useAuth() as { user: { email?: string } | null };
  const { profile, progressSummary } = useStudentProfile();
  const { isDark } = usePlatformTheme();

  const displayName = useLearnerDisplayName() || 'Learner';
  const email = user?.email || platformUser?.email || '';

  const closeMenu = () => onProfileOpenChange(false);

  useDismissOnOutside(profileOpen, menuRef, closeMenu);

  const handleLogout = () => {
    closeMenu();
    onNavigate?.();
    dispatch(logout());
    navigate(ROUTES.HOME);
    if (isSupabaseConfigured) {
      void signOut();
    }
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => onProfileOpenChange(!profileOpen)}
        className="flex items-center gap-2 rounded-lg p-1 transition-colors hover:bg-surface-secondary"
        aria-expanded={profileOpen}
        aria-haspopup="true"
      >
        <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full">
          <LearnerAvatar
            profile={profile}
            progressSummary={progressSummary}
            showEnglish
            size="nav"
            isDark={isDark}
          />
        </span>
      </button>
      {profileOpen ? (
        <div className="absolute right-0 top-full z-50 mt-2 w-56 animate-scale-in rounded-2xl border border-border bg-surface py-2 shadow-modal">
          <div className="border-b border-border px-4 py-2">
            <p className="truncate text-sm font-medium">{displayName}</p>
            <p className="truncate text-caption">{email}</p>
            {platformUser?.institutionNames?.length ? (
              <p className="mt-0.5 truncate text-xs text-content-secondary">
                {platformUser.institutionNames.join(' · ')}
              </p>
            ) : null}
          </div>
          <Link
            to={ROUTES.DASHBOARD}
            className="flex min-h-[44px] items-center gap-2 px-4 py-2 text-sm hover:bg-surface-secondary"
            onClick={() => {
              closeMenu();
              onNavigate?.();
            }}
          >
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </Link>
          <Link
            to={ROUTES.PROFILE}
            className="flex min-h-[44px] items-center gap-2 px-4 py-2 text-sm hover:bg-surface-secondary"
            onClick={() => {
              closeMenu();
              onNavigate?.();
            }}
          >
            <User className="h-4 w-4" /> {learnerNav.progress}
          </Link>
          {onOpenEthics ? (
            <button
              type="button"
              className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-sm hover:bg-surface-secondary"
              onClick={() => {
                closeMenu();
                onOpenEthics();
              }}
            >
              <Scale className="h-4 w-4" />
              <span>{learnerNav.ethics}</span>
            </button>
          ) : null}
          <Link
            to={ROUTES.SETTINGS}
            className="flex min-h-[44px] items-center gap-2 px-4 py-2 text-sm hover:bg-surface-secondary"
            onClick={() => {
              closeMenu();
              onNavigate?.();
            }}
          >
            <Settings className="h-4 w-4" /> Settings
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-sm text-danger hover:bg-surface-secondary"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
